/**
 * Invoice RMA form.
 *
 * Staff-only form accessed from ReturnView when an RMA is in a billable
 * state. Posts billing details, fees, and shipping cost to
 * /rmas/:id/invoice/. The parent ReturnView owns page chrome (AppNav /
 * TitleBar); this component renders only the form body. On success it
 * navigates to /rma/:id/view.
 *
 * Contains:
 * - InvoiceReturnForm — default export, the form component
 */

import { Box, Button, Checkbox, FormControlLabel, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import { List, Map } from 'immutable';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { ContactSelector } from '@sophocles/components/contacts';
import { findAutoFillContact } from '@sophocles/components/contacts/hooks/useAutoFillContact';
import { fetchAllRmaFees } from '@sophocles/entities/RmaFee';
import { fetchAllWarrantyFees } from '@sophocles/entities/WarrantyFee';
import { getAuthUserSelector } from '@sophocles/features/auth/authSelectors';
import { useInvoiceRmaMutation } from '@sophocles/features/entities/rmas';
import { setEntityData } from '@sophocles/features/resources/resourceHandlerActions';
import { getEntityDataSelector } from '@sophocles/features/resources/resourceHandlerSelectors';
import { addToast } from '@sophocles/features/toast';
import { formatUserString } from '@sophocles/utils';
import { parseApiError } from '@sophocles/utils/apiErrors';
import SpecialInstructionsCollapse from '@sophocles/views/returns/components/CustomerSpecialInstructionsCollapse';
import SiteGroupInstructionsCollapse from '@sophocles/views/returns/components/SiteGroupInstructionsCollapse';
import TextDisplay from '@sophocles/views/returns/components/TextDisplay';

import { SectionCard } from '../components/ReturnSection';
import { billContactFields, isValidContactId } from '../utils/contactPayload';
import { returnEntityLabels } from '../utils/tenantLabels';

import RmaFeeMultiSelect from './fields/RmaFeeMultiSelect';
import WarrantyFeeSelect from './fields/WarrantyFeeSelect';
import { isValidShippingCost } from './validation';

// ─── helpers ──────────────────────────────────────────────────────────────────

// Handling-fee policy: a 5% surcharge on the shipping cost plus a flat fee
// that steps up once the shipping cost reaches the tier threshold.
const HANDLING_SURCHARGE_RATE = 0.05;
const HANDLING_TIER_THRESHOLD = 25;
const HANDLING_FEE_LOW = 10;
const HANDLING_FEE_HIGH = 15;

// Filled multiline centers a short string in the box (MuiInputBase-root's
// align-items: center); anchor to the top so typed text lays out from line
// 1 down, matching the pattern SettingsTextField documents.
const MULTILINE_SX = {
  '& .MuiInputBase-root.MuiInputBase-multiline': {
    alignItems: 'flex-start',
  },
  '& .MuiInputBase-inputMultiline': {
    alignSelf: 'flex-start',
    padding: '0 !important',
  },
};

/**
 * Format an ISO date string for display.
 * @param {string|null} iso
 * @returns {string|null}
 */
function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

/**
 * Format an ISO datetime string for display.
 * @param {string|null} iso
 * @returns {string|null}
 */
function fmtDateTime(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString()}, ${d.toLocaleTimeString()}`;
  } catch {
    return iso;
  }
}

/**
 * Seed a billing contact object from an RMA Immutable Map.
 * Returns null when neither FK nor inline data is present.
 *
 * @param {import('immutable').Map} rma
 * @returns {object|null}
 */
function seedBillingContact(rma) {
  const fk = rma.get('billing_contact', null);
  if (fk && fk.get && fk.get('id')) {
    const obj = fk.toJS ? fk.toJS() : fk;
    if (!obj.full_address_display) {
      obj.full_address_display = [
        obj.address,
        obj.address_2,
        [obj.city, obj.state, obj.zipcode].filter(Boolean).join(', '),
        obj.country,
      ]
        .filter(Boolean)
        .join(', ');
    }
    return obj;
  }

  const name = rma.get('bill_contact', '');
  const email = rma.get('bill_email', '');
  const address = rma.get('bill_address', '');
  if (!name && !email && !address) return null;

  return {
    id: null,
    name,
    business_name: rma.get('bill_company_name', ''),
    email,
    phone1: rma.get('bill_phone', ''),
    address,
    address_2: rma.get('bill_address_2', ''),
    city: rma.get('bill_city', ''),
    state: rma.get('bill_state', ''),
    zipcode: rma.get('bill_zip', ''),
    country: rma.get('bill_country', ''),
    _isManualEntry: true,
  };
}

// ─── reference-data hook ──────────────────────────────────────────────────────

function useInvoiceReferenceData() {
  const dispatch = useDispatch();
  const entityData = useSelector(getEntityDataSelector);
  const user = useSelector(getAuthUserSelector);

  useEffect(() => {
    const canViewFees = user.get('is_superuser', false) || user.get('is_staff', false);
    if (!canViewFees) return;

    if (!entityData.has('RmaFees')) {
      dispatch(fetchAllRmaFees()).then(data => dispatch(setEntityData('RmaFees', data)));
    }
    if (!entityData.has('WarrantyFees')) {
      dispatch(fetchAllWarrantyFees()).then(data => dispatch(setEntityData('WarrantyFees', data)));
    }
  }, [dispatch, entityData, user]);
}

// ─── component ────────────────────────────────────────────────────────────────

/**
 * @param {{ rma: import('immutable').Map }} props
 */
export default function InvoiceReturnForm({ rma }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(getAuthUserSelector);

  const [invoiceRma] = useInvoiceRmaMutation();

  useInvoiceReferenceData();

  // ── Derived read-only values from RMA ─────────────────────────────────────

  const rmaId = rma.get('id', null);
  const rmaNumber = rma.get('rma_number', null);
  const rmaStatusDisplay = rma.get('status_display', '');

  const customer = rma.getIn(['site_group', 'customer'], Map());
  const customerId = customer ? customer.get('id', 0) : 0;
  const entityLabels = returnEntityLabels(t, customer);
  const siteGroup = rma.get('site_group', Map());
  const siteGroupId = siteGroup ? siteGroup.get('id', 0) : 0;
  const site = rma.get('site', null);
  const ticket = rma.get('ticket', null);

  const legacyOldIccid = rma.get('legacy_incoming_iccid', null);
  const legacyNewIccid = rma.get('legacy_new_iccid', null);
  const legacyReplacedIccid = rma.get('legacy_replaced_iccid', null);

  const returnedRadio = rma.get('radio_returned_by_user', null);
  const newRadioRma = rma.get('radio_shipped_to_user', null);
  const oldICCID = rma.getIn(['radio_returned_by_user', 'iccid'], null);
  const newICCID = rma.getIn(['radio_shipped_to_user', 'iccid'], null);

  const modelNumber = rma.get('model', null);
  const arrivalFirmwareVersion = rma.get('arrival_firmware_version', null);
  const warrantyStatusName = rma.getIn(['warranty_status', 'name'], null);
  const isQuickShip = rma.get('is_quick_ship', false);
  const trackingNumber = rma.get('tracking_number', '');

  const issueSubCategory = rma.get('issue', null);
  const issueCategory = issueSubCategory ? issueSubCategory.get('category', null) : null;

  const contact = rma.get('contact', null);
  const contactName = contact ? contact.get('name', null) : null;
  const contactTitle = contact ? contact.get('title', null) : null;
  const contactEmail = contact ? contact.get('email', null) : null;

  const pastInventoryAdjustments = rma.get('inventory_adjustments', List());

  const issueTime = rma.get('input_time', null);
  const updateTime = rma.get('update_time', null);
  const shippingDate = rma.get('shipping_date', null);
  const receivedDate = rma.get('received_date', null);
  const invoiceDate = rma.get('invoiced_time', null);
  const issueUser = rma.get('input_user', null);
  const updateUser = rma.get('update_user', null);
  const shippingUser = rma.get('shipping_user', null);
  const receivedUser = rma.get('received_user', null);
  const invoiceUser = rma.get('invoiced_user', null);

  const isStaff = user.get('is_staff', false);

  // ── Formik initialValues ──────────────────────────────────────────────────

  const initialValues = {
    comments: rma.get('comments', '') || '',
    hidden_comments: rma.get('hidden_comments', '') || '',
    shipping_cost: rma.get('shipping_cost', '') || '',
  };

  // ── External-component state ──────────────────────────────────────────────

  const [loading, setLoading] = useState(false);

  const [selectedBillingContact, setSelectedBillingContact] = useState(() => seedBillingContact(rma));

  const [selectedRmaFees, setSelectedRmaFees] = useState(rma.get('additional_fees', List()).map(v => v.get('id', 0)));
  const [warrantyFeeId, setWarrantyFeeId] = useState(rma.getIn(['warranty_fee', 'id'], null));

  const [unauthorizedSwap, setUnauthorizedSwap] = useState(rma.get('unauthorized_swap', false));

  const [shippingCostDisplay, setShippingCostDisplay] = useState(Number(rma.get('shipping_cost', 0)));
  const [shippingCostError, setShippingCostError] = useState(null);

  const [customerSpecialInstructionsFollowed, setCustomerSpecialInstructionsFollowed] = useState(true);
  const [siteGroupInstructionsFollowed, setSiteGroupInstructionsFollowed] = useState(true);

  // ── Auto-fill billing contact if not pre-seeded ───────────────────────────

  useEffect(() => {
    if (selectedBillingContact || !siteGroupId) return;
    findAutoFillContact({ customerId, siteGroupId, addressType: 'billing' })
      .then(contact => {
        if (contact) setSelectedBillingContact(contact);
      })
      .catch(() => {});
  }, []); // runs once on mount

  // ── Fee handlers ──────────────────────────────────────────────────────────

  const handleRmaFeeSelection = useCallback(feeId => setSelectedRmaFees(prev => prev.concat(feeId)), []);

  const handleRmaFeeDeselect = useCallback(index => setSelectedRmaFees(prev => prev.delete(index)), []);

  // ── Derived cost display ──────────────────────────────────────────────────

  const surCharge = shippingCostDisplay * HANDLING_SURCHARGE_RATE;
  const handlingCost =
    shippingCostDisplay === 0
      ? 0
      : shippingCostDisplay < HANDLING_TIER_THRESHOLD
        ? surCharge + HANDLING_FEE_LOW
        : surCharge + HANDLING_FEE_HIGH;
  const shippingAndHandlingCost = Number(handlingCost + shippingCostDisplay);

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(
    (values, { setSubmitting }) => {
      if (!rmaId) {
        setSubmitting(false);
        return;
      }

      if (!isValidShippingCost(values.shipping_cost)) {
        dispatch(
          addToast({
            message: t('rma.shippingCostInvalid', 'Shipping cost must be a valid dollar amount (e.g. 12.50).'),
            level: 'error',
          }),
        );
        setShippingCostError(t('rma.mustBeValidDollar', 'Must be a valid dollar amount (e.g. 12.50)'));
        setSubmitting(false);
        return;
      }

      const payload = {
        comments: values.comments,
        hidden_comments: values.hidden_comments,
        shipping_cost: values.shipping_cost || null,
        unauthorized_swap: unauthorizedSwap,
        warranty_fee_id: warrantyFeeId,
        additional_fees_ids: selectedRmaFees.toArray(),
        billing_address_id: null,
      };

      if (selectedBillingContact) {
        payload.billing_contact =
          selectedBillingContact.id && isValidContactId(selectedBillingContact.id) && !selectedBillingContact._isAddress
            ? selectedBillingContact.id
            : null;

        const siteGroupName = rma.getIn(['site_group', 'name'], '');
        const customerName = rma.getIn(['site_group', 'customer', 'name'], '');
        Object.assign(payload, billContactFields(selectedBillingContact, { siteGroupName, customerName }));
      }

      setLoading(true);
      invoiceRma({ id: rmaId, body: payload })
        .unwrap()
        .then(() => {
          setLoading(false);
          setSubmitting(false);
          dispatch(
            addToast({
              title: t('general.success', 'Success'),
              message: t('rma.rmaInvoiced', 'RMA invoiced'),
              level: 'success',
              dismissible: true,
            }),
          );
          navigate(`/rma/${rmaId}/view`, { replace: true });
        })
        .catch(err => {
          setLoading(false);
          setSubmitting(false);
          const { nonFieldMessages } = parseApiError(err, {
            knownFields: [],
            genericMessage: t('rma.processError', 'Error processing RMA'),
          });
          dispatch(
            addToast({
              message: nonFieldMessages[0] ?? t('rma.processError', 'Error processing RMA'),
              level: 'error',
            }),
          );
        });
    },
    [
      rmaId,
      rma,
      unauthorizedSwap,
      warrantyFeeId,
      selectedRmaFees,
      selectedBillingContact,
      invoiceRma,
      dispatch,
      navigate,
    ],
  );

  // ── canSubmit ─────────────────────────────────────────────────────────────

  const canSubmit =
    selectedBillingContact != null &&
    customerSpecialInstructionsFollowed &&
    siteGroupInstructionsFollowed &&
    !loading &&
    !shippingCostError;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', overflow: 'auto', flex: 1 }}>
      <Box sx={{ width: '100%' }}>
        {/* ── RMA Details ── */}
        <SectionCard
          title={t('rma.rmaDetailsTitle', 'RMA {{num}} Details', { num: rmaNumber ? '# ' + rmaNumber : '' })}
        >
          <Box component="dl" sx={{ m: 0 }}>
            <TextDisplay title={t('rma.rmaStatus', 'RMA Status')}>{rmaStatusDisplay}</TextDisplay>
            {!!customer && (
              <TextDisplay title={entityLabels.customer}>
                {customerId}. {customer.get('name', t('rma.noneLower', 'none'))}{' '}
                <Link style={{ fontSize: '13px' }} target="_blank" to={`/c/${customerId}/plants`}>
                  {t('rma.link', '(link)')}
                </Link>
              </TextDisplay>
            )}
            {!!siteGroup && (
              <TextDisplay title={entityLabels.siteGroup}>
                {siteGroup.get('name', '0')}. {siteGroup.get('description', t('rma.noneLower', 'none'))}{' '}
                <Link
                  style={{ fontSize: '13px' }}
                  target="_blank"
                  to={`/c/${customerId}/plant/${siteGroupId}/structures`}
                >
                  {t('rma.link', '(link)')}
                </Link>
              </TextDisplay>
            )}
            {!!site && (
              <TextDisplay title={entityLabels.structure}>
                {site.get('name', '0')}. {site.get('description', t('rma.noneLower', 'none'))}{' '}
                <Link
                  style={{ fontSize: '13px' }}
                  target="_blank"
                  to={`/c/${customerId}/plant/${siteGroupId}/structure/${site.get('id', 0)}`}
                >
                  {t('rma.link', '(link)')}
                </Link>
              </TextDisplay>
            )}
            {!!ticket && (
              <TextDisplay title={t('rma.supportTicket', 'Support Ticket')}>
                {ticket.get('ticket_number', '')}{' '}
                <Link style={{ fontSize: '13px' }} target="_blank" to={`/support/ticket/${ticket.get('id')}/view`}>
                  {t('rma.link', '(link)')}
                </Link>
              </TextDisplay>
            )}
            {issueTime && (
              <TextDisplay title={t('rma.timeIssuedFull', 'Time Issued')}>
                {fmtDateTime(issueTime)} {isStaff ? ' - ' + formatUserString(issueUser) : null}
              </TextDisplay>
            )}
            {shippingDate && (
              <TextDisplay title={t('rma.dateShippedFull', 'Date Shipped')}>
                {fmtDate(shippingDate)} {isStaff ? ' - ' + formatUserString(shippingUser) : null}
              </TextDisplay>
            )}
            {receivedDate && (
              <TextDisplay title={t('rma.dateProcessedFull', 'Date Processed')}>
                {fmtDate(receivedDate)} {isStaff ? ' - ' + formatUserString(receivedUser) : null}
              </TextDisplay>
            )}
            {invoiceDate && isStaff && (
              <TextDisplay title={t('rma.dateInvoicedStar', 'Date Invoiced*')}>
                {fmtDate(invoiceDate)} - {formatUserString(invoiceUser)}
              </TextDisplay>
            )}
            {updateTime && isStaff && (
              <TextDisplay title={t('rma.lastUpdatedStar', 'Last Updated*')}>
                {fmtDateTime(updateTime)} - {formatUserString(updateUser)}
              </TextDisplay>
            )}
          </Box>
        </SectionCard>

        {/* ── Contact Details (read-only legacy contact display) ── */}
        <SectionCard title={t('rma.contactDetails', 'Contact Details')}>
          {!contact && (
            <TextDisplay title={t('rma.contactLabel', 'Contact')}>
              {t('rma.noContactSelected', 'No Contact Selected')}
            </TextDisplay>
          )}
          <Box component="dl" sx={{ m: 0 }}>
            <TextDisplay title={t('rma.contactName', 'Contact Name')} show={!!contactName}>
              {contactName}
            </TextDisplay>
            <TextDisplay title={t('rma.contactTitle', 'Contact Title')} show={!!contactTitle}>
              {contactTitle}
            </TextDisplay>
            <TextDisplay title={t('rma.contactEmail', 'Contact Email')} show={!!contactEmail}>
              {contactEmail}
            </TextDisplay>
          </Box>
        </SectionCard>

        {/* ── Processing Details (read-only) ── */}
        <SectionCard title={t('rma.processingDetails', 'Processing Details')}>
          <Box component="dl" sx={{ m: 0 }}>
            <TextDisplay title={t('rma.legacyOldIccid', 'Legacy Old ICCID')} show={!!legacyOldIccid}>
              {legacyOldIccid}
            </TextDisplay>
            <TextDisplay title={t('rma.legacyNewIccid', 'Legacy New ICCID')} show={!!legacyNewIccid}>
              {legacyNewIccid}
            </TextDisplay>
            <TextDisplay
              title={t('rma.legacyReplacementIccid', 'Legacy Replacement ICCID')}
              show={!!legacyReplacedIccid}
            >
              {legacyReplacedIccid}
            </TextDisplay>
            <TextDisplay title={t('rma.oldDevice', 'Old Device')} show={!!oldICCID}>
              {returnedRadio != null ? (
                <Link to={`/support/radios/${returnedRadio.get('id', 0)}`}>{oldICCID}</Link>
              ) : (
                oldICCID
              )}
            </TextDisplay>
            <TextDisplay title={t('rma.oldModelHash', 'Old Model #')} show={!!oldICCID}>
              {modelNumber}
            </TextDisplay>
            <TextDisplay title={t('rma.oldFirmwareVersion', 'Old Firmware Version')} show={!!oldICCID}>
              {arrivalFirmwareVersion}
            </TextDisplay>
            <TextDisplay
              title={t('rma.replacementDevice', 'Replacement Device')}
              show={!!(newICCID || !legacyReplacedIccid)}
            >
              {newRadioRma != null ? (
                <Link to={`/support/radios/${newRadioRma.get('id', 0)}`}>{newICCID}</Link>
              ) : (
                t('rma.noReplacementDevice', 'No Replacement Device')
              )}
            </TextDisplay>
            {!!issueCategory && (
              <TextDisplay title={t('rma.issueCategory', 'Category')}>{issueCategory.get('name', '')}</TextDisplay>
            )}
            {!!issueSubCategory && (
              <TextDisplay title={t('rma.issueSubCategory', 'Subcategory')}>
                {issueSubCategory.get('name', '')}
              </TextDisplay>
            )}
            <TextDisplay title={t('rma.warrantyStatus', 'Warranty')} show={!!warrantyStatusName}>
              {warrantyStatusName}
            </TextDisplay>
            <TextDisplay title={t('rma.quickShipRmaQuestion', 'Quick Ship RMA?')}>
              {isQuickShip ? t('general.yes', 'Yes') : t('general.no', 'No')}
            </TextDisplay>
            <TextDisplay title={t('rma.trackingHash', 'Tracking #')}>{trackingNumber}</TextDisplay>
          </Box>
        </SectionCard>

        <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
          {({ values, submitForm, isSubmitting }) => (
            <Form>
              {/* ── Special Instructions ── */}
              {(customerId !== 0 || siteGroupId !== 0) && (
                <SectionCard title={t('rma.specialInstructions', 'Special Instructions')}>
                  {customerId !== 0 && (
                    <SpecialInstructionsCollapse
                      customer={customerId}
                      types={[1, 3]}
                      checked={customerSpecialInstructionsFollowed}
                      onToggle={() => setCustomerSpecialInstructionsFollowed(prev => !prev)}
                    />
                  )}
                  {siteGroupId !== 0 && (
                    <SiteGroupInstructionsCollapse
                      siteGroup={siteGroupId}
                      types={[1, 3]}
                      checked={siteGroupInstructionsFollowed}
                      onToggle={() => setSiteGroupInstructionsFollowed(prev => !prev)}
                    />
                  )}
                </SectionCard>
              )}

              {/* ── Inventory Changes (read-only display) ── */}
              <SectionCard title={t('rma.inventoryChanges', 'Inventory Changes')}>
                {pastInventoryAdjustments.isEmpty() ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('rma.noInventoryAdjustments', 'No Inventory Adjustments')}
                  </Typography>
                ) : (
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {t('rma.previousInventoryAdjustments', 'Previous Inventory Adjustments')}
                      </Typography>
                      <Tooltip
                        title={t(
                          'rma.inventoryAdjustmentsTooltip',
                          'These inventory adjustments have already been made and cannot be reverted.',
                        )}
                        placement="top"
                      >
                        <Box component="span" sx={{ color: 'text.secondary', fontSize: '14px', cursor: 'default' }}>
                          ⓘ
                        </Box>
                      </Tooltip>
                    </Stack>
                    <Box component="ul" sx={{ pl: 2, mt: 0 }}>
                      {pastInventoryAdjustments.map((item, index) => (
                        <li key={index}>
                          {item.getIn(['product', 'item_part_num'], t('rma.noPartNumber', 'no part number'))} -{' '}
                          {item.getIn(['product', 'item_name'], t('rma.noProductName', 'no product name'))} -{' '}
                          {item.get('quantity_shipped', 0) > 0
                            ? t('rma.removedQty', 'Removed: {{qty}}', { qty: item.get('quantity_shipped', 0) })
                            : t('rma.addedQty', 'Added {{qty}}', { qty: item.get('quantity_added_to_inventory', 0) })}
                        </li>
                      ))}
                    </Box>
                  </Box>
                )}
              </SectionCard>

              {/* ── Notes ── */}
              <SectionCard title={t('rma.notes', 'Notes')}>
                <Field name="comments">
                  {({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={4}
                      margin="normal"
                      label={t('rma.comments', 'Comments')}
                      inputProps={{ maxLength: 2048 }}
                      sx={MULTILINE_SX}
                    />
                  )}
                </Field>
                <Field name="hidden_comments">
                  {({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={4}
                      margin="normal"
                      label={t('rma.internalCommentsHidden', 'Internal Comments (hidden from customer)')}
                      inputProps={{ maxLength: 2048 }}
                      sx={MULTILINE_SX}
                    />
                  )}
                </Field>
              </SectionCard>

              {/* ── Billing ── */}
              <SectionCard title={t('rma.billing', 'Billing')}>
                <Box sx={{ mb: 2 }}>
                  <ContactSelector
                    value={selectedBillingContact}
                    onChange={setSelectedBillingContact}
                    customerId={customerId}
                    siteGroupId={siteGroupId}
                    customer={customer}
                    siteGroup={siteGroup}
                    label={t('rma.billingAddress', 'Billing Address')}
                    addressType="billing"
                    required={true}
                    showAddress={true}
                    requiredFields={['address', 'name']}
                    primaryContext="rma"
                    contextLabel={t('rma.invoice', 'Invoice')}
                    showDefaultAddresses={true}
                  />
                </Box>

                <Box sx={{ mt: 1 }}>
                  <FormControlLabel
                    label={t('rma.unauthorizedSwap', 'Unauthorized Swap')}
                    control={
                      <Checkbox checked={unauthorizedSwap} onChange={() => setUnauthorizedSwap(prev => !prev)} />
                    }
                  />
                </Box>
              </SectionCard>

              {/* ── Fees ── */}
              <SectionCard title={t('rma.fees', 'Fees')}>
                <Box sx={{ mb: 2 }}>
                  <WarrantyFeeSelect
                    value={warrantyFeeId}
                    onChange={setWarrantyFeeId}
                    label={t('rma.warrantyFee', 'Warranty Fee')}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {t('rma.shippingCost', 'Shipping Cost')}
                  </Typography>
                  <Field name="shipping_cost">
                    {({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        // No visible label — the "Shipping Cost" heading above
                        // already names the field; hiddenLabel drops the
                        // filled variant's reserved label padding.
                        hiddenLabel
                        onChange={e => {
                          field.onChange(e);
                          const val = e.target.value;
                          setShippingCostDisplay(Number(val) || 0);
                          if (!isValidShippingCost(val)) {
                            setShippingCostError(
                              t('rma.mustBeValidDollar', 'Must be a valid dollar amount (e.g. 12.50)'),
                            );
                          } else {
                            setShippingCostError(null);
                          }
                        }}
                        error={!!shippingCostError}
                        helperText={shippingCostError}
                        inputProps={{ maxLength: 10 }}
                        sx={{ width: 256 }}
                      />
                    )}
                  </Field>
                </Box>

                <Box sx={{ mb: 1, fontSize: '14px' }}>
                  <Typography variant="body2">
                    <b>{t('rma.handlingCostColon', 'Handling Cost:')}</b> ${handlingCost.toFixed(2)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2, fontSize: '14px' }}>
                  <Typography variant="body2">
                    <b>{t('rma.shippingHandlingCostColon', 'Shipping & Handling Cost:')}</b> $
                    {shippingAndHandlingCost.toFixed(2)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <RmaFeeMultiSelect
                    selected={selectedRmaFees}
                    onAdd={handleRmaFeeSelection}
                    onRemove={handleRmaFeeDeselect}
                  />
                </Box>
              </SectionCard>

              {/* ── Actions ── */}
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1, pb: 2 }}>
                <Button variant="outlined" color="inherit" onClick={() => navigate(-1)}>
                  {t('general.cancel', 'Cancel')}
                </Button>
                <Button variant="contained" color="primary" disabled={!canSubmit || isSubmitting} onClick={submitForm}>
                  {t('rma.invoiceRma', 'Invoice RMA')}
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
}
